import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const ORDER_NUMBER_RANDOM_RANGE = 9000;
const ORDER_NUMBER_BASE = 1000;

const makeSpec = () => ({ id: `spec_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, key: "", value: "" });

export default function AdminNewOrder() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    order_number: `MT-${new Date().getFullYear()}-${String(Math.floor(Math.random() * ORDER_NUMBER_RANDOM_RANGE) + ORDER_NUMBER_BASE)}`,
    client_email: "client@buildcorp.com",
    client_name: "BuildCorp Construction",
    product_name: "",
    product_type: "Glass Shutter Door",
    project_name: "",
    quantity: 1,
    unit_price: 0,
    expected_delivery: "",
    warranty_info: "10-year structural warranty on frame, 5-year on glass panels",
    hero_image_url: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg",
  });
  const [specs, setSpecs] = useState([makeSpec()]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.product_name || !form.expected_delivery) {
      toast.error("Fill product name and expected delivery");
      return;
    }
    const specifications = {};
    specs.forEach((s) => { if (s.key.trim()) specifications[s.key.trim()] = s.value; });

    setBusy(true);
    try {
      const res = await api.post("/orders", {
        ...form,
        quantity: Number(form.quantity),
        unit_price: Number(form.unit_price),
        specifications,
      });
      toast.success(`Order ${res.data.order_number} created`);
      navigate(`/admin/orders/${res.data.order_id}`);
    } catch (e) {
      toast.error("Failed to create order");
    }
    setBusy(false);
  };

  return (
    <Layout>
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 mt-tiny text-zinc-500 hover:text-zinc-900 mb-6" data-testid="new-order-back">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="mb-8">
        <div className="mt-tiny text-[#FF5A1F]">Factory Console</div>
        <h1 className="font-display text-4xl font-black tracking-tight text-zinc-900 mt-2">Create New Order</h1>
      </div>

      <div className="bg-white border border-zinc-200 p-6 md:p-10 max-w-4xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Order Number" testId="input-order-number">
            <Input value={form.order_number} onChange={(e) => setField("order_number", e.target.value)} className="rounded-none h-11 border-zinc-200" data-testid="order-number-input" />
          </Field>
          <Field label="Product Type" testId="input-product-type">
            <Input value={form.product_type} onChange={(e) => setField("product_type", e.target.value)} className="rounded-none h-11 border-zinc-200" data-testid="product-type-input" />
          </Field>
          <Field label="Client Email">
            <Input type="email" value={form.client_email} onChange={(e) => setField("client_email", e.target.value)} className="rounded-none h-11 border-zinc-200" data-testid="client-email-input" />
          </Field>
          <Field label="Client Name">
            <Input value={form.client_name} onChange={(e) => setField("client_name", e.target.value)} className="rounded-none h-11 border-zinc-200" data-testid="client-name-input" />
          </Field>
          <Field label="Product Name" full>
            <Input value={form.product_name} onChange={(e) => setField("product_name", e.target.value)} className="rounded-none h-11 border-zinc-200" placeholder="Premium Automatic Glass Shutter Door" data-testid="product-name-input" />
          </Field>
          <Field label="Project Name" full>
            <Input value={form.project_name} onChange={(e) => setField("project_name", e.target.value)} className="rounded-none h-11 border-zinc-200" placeholder="Marina Tower — Phase 2" data-testid="project-name-input" />
          </Field>
          <Field label="Quantity">
            <Input type="number" min="1" value={form.quantity} onChange={(e) => setField("quantity", e.target.value)} className="rounded-none h-11 border-zinc-200" data-testid="quantity-input" />
          </Field>
          <Field label="Unit Price (AED)">
            <Input type="number" min="0" step="0.01" value={form.unit_price} onChange={(e) => setField("unit_price", e.target.value)} className="rounded-none h-11 border-zinc-200" data-testid="unit-price-input" />
          </Field>
          <Field label="Expected Delivery">
            <Input type="date" value={form.expected_delivery} onChange={(e) => setField("expected_delivery", e.target.value)} className="rounded-none h-11 border-zinc-200" data-testid="expected-delivery-input" />
          </Field>
          <Field label="Hero Image URL">
            <Input value={form.hero_image_url} onChange={(e) => setField("hero_image_url", e.target.value)} className="rounded-none h-11 border-zinc-200" data-testid="hero-image-input" />
          </Field>
          <Field label="Warranty Info" full>
            <Textarea value={form.warranty_info} onChange={(e) => setField("warranty_info", e.target.value)} className="rounded-none border-zinc-200 min-h-[80px]" data-testid="warranty-input" />
          </Field>
        </div>

        <div className="mt-10 pt-8 border-t border-zinc-200">
          <div className="mt-tiny text-zinc-500 mb-3">Specifications</div>
          <div className="space-y-2">
            {specs.map((s, i) => (
              <div key={s.id} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2">
                <Input
                  placeholder="e.g. Dimensions"
                  value={s.key}
                  onChange={(e) => setSpecs(specs.map((sp) => sp.id === s.id ? { ...sp, key: e.target.value } : sp))}
                  className="rounded-none h-11 border-zinc-200"
                  data-testid={`spec-key-${i}`}
                />
                <Input
                  placeholder="e.g. 2400mm × 3200mm"
                  value={s.value}
                  onChange={(e) => setSpecs(specs.map((sp) => sp.id === s.id ? { ...sp, value: e.target.value } : sp))}
                  className="rounded-none h-11 border-zinc-200"
                  data-testid={`spec-value-${i}`}
                />
                <button
                  onClick={() => setSpecs(specs.filter((sp) => sp.id !== s.id))}
                  className="border border-zinc-200 hover:border-red-500 hover:text-red-500 p-3 transition-colors"
                  data-testid={`remove-spec-${i}`}
                >
                  <Trash size={16} />
                </button>
              </div>
            ))}
            <button
              onClick={() => setSpecs([...specs, makeSpec()])}
              className="inline-flex items-center gap-2 mt-2 border border-zinc-200 hover:border-zinc-900 px-4 py-2 text-sm"
              data-testid="add-spec-button"
            >
              <Plus size={14} /> Add specification
            </button>
          </div>
        </div>

        <div className="mt-10 flex justify-end">
          <button
            onClick={submit}
            disabled={busy}
            data-testid="create-order-button"
            className="bg-[#FF5A1F] hover:bg-[#E04814] text-white px-8 py-3 font-medium transition-colors disabled:opacity-60"
          >
            {busy ? "Creating..." : "Create Order"}
          </button>
        </div>
      </div>
    </Layout>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="mt-tiny text-zinc-500 mb-2">{label}</div>
      {children}
    </div>
  );
}
